/**
 * The single gameplay socket (networking.md "Connection Lifecycle").
 *
 * Owns: envelope sequence numbers, hello/resume handshakes, RTT pings, and
 * automatic resume-with-backoff after an unexpected close. The envelope seq
 * keeps counting across resumes (the zone's lastProcessedSeq survives the
 * socket); only a fresh hello resets the stream.
 */

import { PROTOCOL_VERSION, SNAPSHOT_BIN1, decodeSnapshotBin } from '@great-cloud/shared/client';
import type {
  ClientMessage,
  GameEvent,
  HelloAckPayload,
  InputPayload,
  ServerMessage,
  SnapshotPayload,
} from '@great-cloud/shared/client';

import {
  clearTimer,
  createGameSocket,
  intervalTimer,
  serverWsUrl,
  socketOpen,
  timeoutTimer,
} from './platform.js';
import type { GameSocket, TimerHandle } from './platform.js';
import { redirectWsUrl } from './channel.js';

/**
 * `conflict` (`q-duplicate-login-warning`): the server answered `alreadyLoggedIn` — the account
 * is live on another device. Nothing entered the world; the connection waits for the player's
 * choice (`takeOver()` or `backOut()`) and never retries on its own.
 */
export type ConnectionPhase =
  | 'connecting'
  | 'playing'
  | 'resuming'
  | 'conflict'
  | 'closed'
  | 'failed';

export interface ConnectionCallbacks {
  onReady(ack: HelloAckPayload, resumed: boolean): void;
  onSnapshot(tick: number, snapshot: SnapshotPayload): void;
  onEvent(tick: number, seq: number, event: GameEvent): void;
  onPhase(phase: ConnectionPhase, detail?: string): void;
  onRtt(rttMs: number): void;
  onNotice(message: string): void;
  /**
   * The server kicked this connection (`kick.d.reason`), after which it never reconnects.
   * `sessionTakenOver` / `loggedInElsewhere` (KICK_REASON_*) send the player back to login.
   */
  onKicked?(reason: string): void;
  /**
   * Every inbound server message (PXL). The client store routes these into its
   * panel/HUD slices; the lifecycle callbacks above still fire for the renderer.
   */
  onAny?(msg: ServerMessage): void;
  /**
   * The guest demo's hard wall answered (AD-794 §3.2): a `guest` session sent a message type that
   * is not in the demo; `type` is the refused type. Routed HERE and never to `onNotice`, so a
   * walled press is one calm line from the caller (`guest/guestWalls.ts` `WallNotifier`), not an
   * error string per press. A wall is never a failure: the connection plays on.
   */
  onGuestWall?(type: string): void;
}

const PING_INTERVAL_MS = 2000;
const RESUME_BACKOFF_MS = [500, 1000, 2000, 4000, 8000];
/** Hello attempts after a close before the first helloAck, then the connection fails. */
const HELLO_RETRY_LIMIT = 5;

export class GameConnection {
  private ws: GameSocket | null = null;
  private seq = 0;
  private lastEventSeq = 0;
  private phase: ConnectionPhase = 'connecting';
  private pingTimer: TimerHandle | null = null;
  private reconnectTimer: TimerHandle | null = null;
  private resumeAttempt = 0;
  private disconnectedAtMs = 0;
  private resumeWindowMs = 60_000;
  private intentionalClose = false;
  /** A helloAck/resumeAck has arrived at least once: a session exists to resume. */
  private established = false;
  /** The player chose "end the other session and play here": hellos carry `takeover: true`. */
  private takeover = false;
  rttMs = 0;

  constructor(
    private readonly token: string,
    private readonly characterId: number,
    private readonly callbacks: ConnectionCallbacks,
    /** Current client content-pack hash (P4.9); sent in hello so the server can compare. */
    private readonly getContentHash: () => string = () => '',
    /**
     * The snapshot rate to ask for (D1; `net/snapshotRate.ts`, the `?snaphz=` URL parameter).
     * Undefined ⇒ nothing is asked and every hello/resume is byte-identical to before D1.
     */
    private readonly snapshotHz?: number,
    /**
     * D4 `q-snapshot-binary-wire`: advertise `snapshotBin1`, so a server that grants it sends
     * snapshots as binary frames (decoded here into the same message the JSON path yields).
     * False ⇒ the hello/resume carry no capability list and snapshots stay JSON (`?snapbin=0`,
     * `net/snapshotRate.ts`). Default false so a constructor call that does not opt in is
     * byte-identical to before D4; the game screen opts in.
     */
    private readonly binarySnapshots = false,
    /**
     * A8b `q-channel-endpoint-and-redirect`: the socket URL for a hello — `net/channel.ts`
     * `resolveGameWsUrl` asks which channel process to open. A resume reuses the URL its session
     * was opened on (the session lives in that process). Default: `/ws`, synchronously, so a
     * constructor call that does not opt in is byte-identical to before A8b.
     */
    private readonly helloUrl: () => string | Promise<string> = serverWsUrl,
    /**
     * `q-channel-resume-reask-dead-process` (residue (6) of A8b-2): asked again when a resume's
     * socket closes without ever opening — the session's channel process is gone (the proxy
     * refuses the upgrade), so retrying its URL until the resume window lapses only spends the
     * window. A named channel replaces the session URL (that process answers the resume with
     * `resumeExpired`, then the fallback hello); null — no answer — keeps it, so a network blip
     * never moves a live session to another process. Default: none, so the resume URL never
     * changes (as before).
     */
    private readonly channelAsk?: () => Promise<string | null>,
  ) {}

  /** The URL the current session was opened on; a resume goes back to it. */
  private sessionUrl: string | null = null;
  /** Bumped by every open, so an ask that resolves after a newer open (or a close) is dropped. */
  private openGen = 0;
  /**
   * A8b-2: the channel process a `redirect` named. The server closes the socket right after it;
   * that close opens a fresh hello HERE instead of a resume (the session moved, it did not drop).
   */
  private redirectUrl: string | null = null;
  /** A resume socket closed without opening: the next resume asks for its channel first. */
  private reaskBeforeResume = false;

  /** D4: the capability list to spread into a hello/resume payload (empty when not opted in). */
  private capabilityRequest(): { clientCapabilities?: string[] } {
    return this.binarySnapshots ? { clientCapabilities: [SNAPSHOT_BIN1] } : {};
  }

  /** D1: the `snapshotHz` request to spread into a hello/resume payload (empty when none). */
  private rateRequest(): { snapshotHz?: number } {
    return this.snapshotHz === undefined ? {} : { snapshotHz: this.snapshotHz };
  }

  connect(): void {
    this.open('hello');
  }

  private open(mode: 'hello' | 'resume'): void {
    this.setPhase(mode === 'hello' ? 'connecting' : 'resuming');
    const gen = ++this.openGen;
    if (mode === 'resume' && this.sessionUrl !== null) {
      const ask = this.reaskBeforeResume ? this.channelAsk : undefined;
      this.reaskBeforeResume = false;
      if (ask === undefined) {
        this.attach(this.sessionUrl, mode);
        return;
      }
      const stale = this.sessionUrl;
      void ask()
        .catch(() => null)
        .then((answered) => {
          if (this.intentionalClose || gen !== this.openGen) return;
          this.sessionUrl = answered ?? stale;
          this.attach(this.sessionUrl, mode);
        });
      return;
    }
    const url = this.helloUrl();
    if (typeof url === 'string') {
      this.sessionUrl = url;
      this.attach(url, mode);
      return;
    }
    void url
      .catch(() => serverWsUrl())
      .then((resolved) => {
        // Closed (or re-opened) while the ask was in flight: this open is spent.
        if (this.intentionalClose || gen !== this.openGen) return;
        this.sessionUrl = resolved;
        this.attach(resolved, mode);
      });
  }

  private attach(url: string, mode: 'hello' | 'resume'): void {
    const ws = createGameSocket(url);
    this.ws = ws;
    let opened = false;

    ws.onopen = () => {
      opened = true;
      if (mode === 'hello') {
        this.seq = 0;
        this.lastEventSeq = 0;
        this.sendRaw({
          v: PROTOCOL_VERSION,
          t: 'hello',
          s: this.nextSeq(),
          d: {
            protocolVersion: PROTOCOL_VERSION,
            token: this.token,
            characterId: this.characterId,
            contentHash: this.getContentHash(),
            ...(this.takeover ? { takeover: true } : {}),
            ...this.rateRequest(),
            ...this.capabilityRequest(),
          },
        });
      } else {
        this.sendRaw({
          v: PROTOCOL_VERSION,
          t: 'resume',
          s: this.nextSeq(),
          d: {
            protocolVersion: PROTOCOL_VERSION,
            token: this.token,
            characterId: this.characterId,
            lastSeq: this.lastEventSeq,
            ...this.rateRequest(),
            ...this.capabilityRequest(),
          },
        });
      }
    };

    ws.onmessage = (msgEvent: MessageEvent) => {
      const data: unknown = msgEvent.data;
      let msg: ServerMessage;
      if (typeof data !== 'string') {
        // D4: a binary frame is a `snapshotBin1` snapshot (only ever sent after this client
        // advertised the capability and the server granted it).
        if (!(data instanceof ArrayBuffer)) return;
        try {
          msg = decodeSnapshotBin(new Uint8Array(data));
        } catch {
          // A snapshot we cannot read leaves our world behind the server's delta baseline: drop
          // the socket so the resume path re-syncs us with a full snapshot.
          ws.close();
          return;
        }
      } else {
        try {
          msg = JSON.parse(data) as ServerMessage;
        } catch {
          return;
        }
      }
      this.onServerMessage(msg, mode);
    };

    ws.onclose = () => {
      this.stopPing();
      if (this.intentionalClose) {
        this.setPhase('closed');
        return;
      }
      if (this.redirectUrl !== null && this.ws === ws) {
        const url = this.redirectUrl;
        this.redirectUrl = null;
        // A failed hello there resumes on the ordinary clock, counted from this move.
        this.disconnectedAtMs = Date.now();
        this.resumeAttempt = 0;
        this.openGen += 1;
        this.sessionUrl = url;
        this.setPhase('connecting', 'redirect');
        this.attach(url, 'hello');
        return;
      }
      // Waiting on the player's choice: a closed socket (the server's decision window lapsed)
      // is not retried — `takeOver()` opens a fresh one if the player still wants in.
      if (this.phase === 'conflict') {
        if (this.ws === ws) this.ws = null;
        return;
      }
      if (!this.established) {
        // Closed before the first helloAck: there is no session to resume (and the
        // resume clock never started, so the window check would fail at once). Retry
        // the hello itself on the same backoff, a bounded number of times.
        this.scheduleHelloRetry();
        return;
      }
      if (this.phase === 'playing') {
        this.disconnectedAtMs = Date.now();
        this.resumeAttempt = 0;
      }
      // Never opened: nothing answers at the session's URL, so ask where the account lives now.
      if (mode === 'resume' && !opened) this.reaskBeforeResume = true;
      this.scheduleResume();
    };

    ws.onerror = () => {
      // close fires next; nothing to do here.
    };
  }

  private onServerMessage(msg: ServerMessage, mode: 'hello' | 'resume'): void {
    // Feed the client store every inbound message (the panels/HUD data backbone).
    this.callbacks.onAny?.(msg);
    switch (msg.t) {
      case 'helloAck':
      case 'resumeAck':
        this.established = true;
        this.resumeAttempt = 0;
        this.resumeWindowMs = msg.d.resumeWindowMs;
        this.setPhase('playing');
        this.startPing();
        this.callbacks.onReady(msg.d, msg.t === 'resumeAck');
        return;
      case 'snapshot':
        this.callbacks.onSnapshot(msg.tick, msg.d);
        return;
      case 'event':
        if (msg.seq > this.lastEventSeq) this.lastEventSeq = msg.seq;
        this.callbacks.onEvent(msg.tick, msg.seq, msg.d);
        return;
      case 'pong':
        this.rttMs = Math.max(0, Date.now() - msg.d.t0);
        this.callbacks.onRtt(this.rttMs);
        return;
      case 'systemNotice':
        this.callbacks.onNotice(msg.d.message);
        return;
      case 'error':
        if (msg.d.code === 'resumeExpired' && mode === 'resume') {
          // Session gone: fall back to a fresh hello on this same socket. A fresh hello
          // restarts the stream, so the counters reset BEFORE it takes its seq — sent
          // after, the hello carried the old high seq and the zone then dropped every
          // input (seq 1, 2, …) as stale.
          this.seq = 0;
          this.lastEventSeq = 0;
          this.sendRaw({
            v: PROTOCOL_VERSION,
            t: 'hello',
            s: this.nextSeq(),
            d: {
              protocolVersion: PROTOCOL_VERSION,
              token: this.token,
              characterId: this.characterId,
              contentHash: this.getContentHash(),
              ...this.rateRequest(),
            },
          });
          return;
        }
        if (msg.d.code === 'guest_wall') {
          this.callbacks.onGuestWall?.(msg.d.message);
          return;
        }
        if (msg.d.code === 'alreadyLoggedIn') {
          // q-duplicate-login-warning: live on another device — ask, never retry.
          this.setPhase('conflict');
          return;
        }
        if (msg.d.code === 'rateLimited' && this.takeover && !this.established) {
          // The account changed devices a moment ago: back to the choice, said plainly.
          this.setPhase('conflict', 'rateLimited');
          return;
        }
        this.callbacks.onNotice(`${msg.d.code}: ${msg.d.message}`);
        if (msg.d.code === 'authFailed' || msg.d.code === 'characterNotFound') {
          this.fail(msg.d.code);
        }
        return;
      case 'redirect':
        this.redirectUrl = redirectWsUrl(this.sessionUrl ?? serverWsUrl(), msg.d.channel);
        return;
      case 'kick':
        // The server's decision window lapsed while the player reads the dialog: the dialog
        // stays; `takeOver()` opens a fresh socket.
        if (this.phase === 'conflict') return;
        this.callbacks.onNotice(`disconnected: ${msg.d.reason}`);
        this.fail(`kicked: ${msg.d.reason}`);
        this.callbacks.onKicked?.(msg.d.reason);
        return;
    }
  }

  private scheduleResume(): void {
    if (this.intentionalClose || this.phase === 'failed') return;
    const elapsed = Date.now() - this.disconnectedAtMs;
    if (elapsed > this.resumeWindowMs + 5000) {
      this.fail('resume window elapsed');
      return;
    }
    const backoff =
      RESUME_BACKOFF_MS[Math.min(this.resumeAttempt, RESUME_BACKOFF_MS.length - 1)] ?? 8000;
    this.resumeAttempt += 1;
    this.setPhase('resuming', `attempt ${this.resumeAttempt}`);
    this.reconnectTimer = timeoutTimer(() => this.open('resume'), backoff);
  }

  private scheduleHelloRetry(): void {
    if (this.intentionalClose || this.phase === 'failed') return;
    if (this.resumeAttempt >= HELLO_RETRY_LIMIT) {
      this.fail('could not connect');
      return;
    }
    const backoff =
      RESUME_BACKOFF_MS[Math.min(this.resumeAttempt, RESUME_BACKOFF_MS.length - 1)] ?? 8000;
    this.resumeAttempt += 1;
    this.setPhase('connecting', `retry ${this.resumeAttempt}`);
    this.reconnectTimer = timeoutTimer(() => this.open('hello'), backoff);
  }

  private fail(detail: string): void {
    this.setPhase('failed', detail);
    this.dispose();
  }

  private nextSeq(): number {
    this.seq += 1;
    return this.seq;
  }

  /** Send a movement input; returns the envelope seq used (= input identity). */
  sendInput(input: InputPayload): number | null {
    if (this.phase !== 'playing' || !this.ws || !socketOpen(this.ws)) return null;
    const s = this.nextSeq();
    this.sendRaw({ v: PROTOCOL_VERSION, t: 'input', s, d: input });
    return s;
  }

  /** Send a basic attack intent (P4.6); the server resolves the nearest enemy in arc/reach. */
  sendAttack(): void {
    if (this.phase !== 'playing' || !this.ws || !socketOpen(this.ws)) return;
    this.sendRaw({ v: PROTOCOL_VERSION, t: 'attackIntent', s: this.nextSeq(), d: {} });
  }

  /**
   * Send any client intent (PXL panels/HUD). Typed so the payload must match the
   * message type (the ClientMessage union enforces t↔d). A no-op unless playing;
   * the server validates and ignores stale/duplicate sequence numbers.
   */
  send<T extends ClientMessage['t']>(t: T, d: Extract<ClientMessage, { t: T }>['d']): void {
    if (this.phase !== 'playing' || !this.ws || !socketOpen(this.ws)) return;
    this.sendRaw({ v: PROTOCOL_VERSION, t, s: this.nextSeq(), d } as ClientMessage);
  }

  private sendRaw(msg: ClientMessage): void {
    if (!this.ws || !socketOpen(this.ws)) return;
    this.ws.send(JSON.stringify(msg));
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = intervalTimer(() => {
      this.sendRaw({ v: PROTOCOL_VERSION, t: 'ping', s: this.nextSeq(), d: { t0: Date.now() } });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearTimer(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private setPhase(phase: ConnectionPhase, detail?: string): void {
    this.phase = phase;
    this.callbacks.onPhase(phase, detail);
  }

  get currentPhase(): ConnectionPhase {
    return this.phase;
  }

  /**
   * "End the other session and play here" (`q-duplicate-login-warning`): re-send the hello with
   * `takeover: true` — on the waiting socket if it is still open, else on a fresh one.
   */
  takeOver(): void {
    if (this.phase !== 'conflict') return;
    this.takeover = true;
    if (this.ws && socketOpen(this.ws)) {
      this.setPhase('connecting', 'takeover');
      this.sendRaw({
        v: PROTOCOL_VERSION,
        t: 'hello',
        s: this.nextSeq(),
        d: {
          protocolVersion: PROTOCOL_VERSION,
          token: this.token,
          characterId: this.characterId,
          contentHash: this.getContentHash(),
          takeover: true,
          ...this.rateRequest(),
        },
      });
      return;
    }
    this.open('hello');
  }

  /** "Back out": leave without entering; the other device keeps playing. */
  backOut(): void {
    this.close();
  }

  close(): void {
    this.intentionalClose = true;
    this.dispose();
    this.setPhase('closed');
  }

  private dispose(): void {
    this.stopPing();
    clearTimer(this.reconnectTimer);
    if (this.ws && socketOpen(this.ws)) this.ws.close(1000);
    this.ws = null;
  }
}
